import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"

import { trackSlide } from "areas/product/store/actions"
import Button from "components/ui/button"
import { Carousel, CarouselContent, CarouselItem } from "components/ui/carousel"
import Image from "components/ui/image"
import Shimmer from "components/ui/shimmer"
import { isTestMode } from "helpers/application"
import { isVideoView, isJ3dView, isTgaiView } from "helpers/gallery-slide-view"
import { getImgSrcSet } from "helpers/image"
import { setQueryParameter } from "helpers/url"
import { useNavigation } from "helpers/use-navigation"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

const Thumbnail = ({
  index,
  onClick,
  productTitle,
  selected,
  slide,
  tabIndex,
  view,
}) => {
  const { isMobile } = useWindowDimensions()
  const keyboardProps = useNavigation({
    direction: isMobile ? "horizontal" : "vertical",
  })

  const lazyload = index > 4
  const imgProps = { lazyload }
  if (isVideoView(slide.view)) {
    imgProps["src"] = require("images/product/vr/videoicon.png")
  } else {
    const slideSrc = slide.raw_src || slide.src || ""
    const dimKey = slideSrc.includes(sl.config.jewlr_scene7_url) ? "wid" : "dim"
    imgProps["src"] = setQueryParameter(slideSrc, dimKey, 46)
    imgProps["srcSet"] = getImgSrcSet(slideSrc, 1024, {
      dim_key: dimKey,
      sizes: [46, 92, 138],
    })
  }
  if (lazyload) {
    imgProps["data-sizes"] = "auto"
  } else {
    imgProps["sizes"] = "46px"
  }

  return (
    <Button
      aria-label={`Go to slide ${index + 1}`}
      className={cn(
        "hover:border-grey-5 focus:border-primary border-3 focus:outline-hidden relative block aspect-square h-auto w-full",
        "has-[.lazyload]:border-grey-10",
        selected ? "border-grey-10" : "border-transparent"
      )}
      onClick={onClick}
      tabIndex={tabIndex}
      {...(selected && { "aria-current": true })}
      {...keyboardProps}
    >
      {isTgaiView(view) ? (
        <div className="size-full" id={`tangiblee_thumbnail_wrapper_${view}`} />
      ) : (
        <Image
          alt={productTitle}
          className={cn(
            "h-auto w-full",
            slide.view === "tangiblee_view" &&
              (sl.config.env == "staging"
                ? "js-tangiblee-cta-v2"
                : "js-tangiblee-cta")
          )}
          data-cy={`product-main-slider-thumbnail-img-${index}`}
          height="46"
          width="46"
          {...imgProps}
        />
      )}
    </Button>
  )
}

Thumbnail.propTypes = {
  index: PropTypes.number,
  onClick: PropTypes.func,
  productTitle: PropTypes.string,
  selected: PropTypes.bool,
  slide: PropTypes.object,
  tabIndex: PropTypes.string,
  view: PropTypes.string,
}

const Thumbnails = ({
  currentSlideIndex,
  j3dSlideIndex,
  productTitle,
  slidesArray,
  styleCode,
}) => {
  const dispatch = useDispatch()
  const wizardLoading = useSelector((state) => state.wizard.loading)
  const { isMobile } = useWindowDimensions()

  const handleThumbnailClick = useCallback(
    (index) => {
      if (!isTestMode && isMobile) {
        dataLayer.push({ event: "clicked-mobile-image-thumbnail" })
      }
      dispatch(trackSlide(index))
    },
    [dispatch, isMobile]
  )

  return (
    <Carousel
      aria-label={`View different images of ${productTitle}`}
      className="flex flex-col"
      description="Product Image Thumbnails"
      disableSkip
      opts={{
        active: true,
        axis: "x",
        breakpoints: {
          "(min-width: 768px)": {
            active: false,
            axis: "y",
          },
        },
        slidesToScroll: "auto",
      }}
    >
      <CarouselContent
        className="tablet:flex-col tablet:gap-12 tablet:pl-0 gap-8 pl-16"
        viewportClassName="w-full"
      >
        <FocusScope>
          {(wizardLoading
            ? [...Array(6)]
            : slidesArray?.length > 0
              ? slidesArray.filter(({ view }) => !isJ3dView(view))
              : []
          ).map((slide, index) => (
            <CarouselItem
              className={cn("basis-52", isTgaiView(slide?.view) && "hidden")}
              key={`mainthumb-${styleCode}-${index}`}
              {...(isTgaiView(slide?.view) && {
                id: `tangiblee_thumbnail_button_${slide?.view}`,
              })}
            >
              {wizardLoading ? (
                <Shimmer className="aspect-square" />
              ) : (
                <Thumbnail
                  index={index}
                  onClick={() => handleThumbnailClick(index)}
                  productTitle={productTitle}
                  selected={currentSlideIndex === index}
                  slide={slide}
                  tabIndex={
                    currentSlideIndex === index ||
                    (currentSlideIndex === j3dSlideIndex && index === 0)
                      ? "0"
                      : "-1"
                  }
                  view={slide?.view}
                />
              )}
            </CarouselItem>
          ))}
        </FocusScope>
      </CarouselContent>
    </Carousel>
  )
}

Thumbnails.propTypes = {
  currentSlideIndex: PropTypes.number,
  j3dSlideIndex: PropTypes.number,
  productTitle: PropTypes.string,
  slidesArray: PropTypes.array,
  styleCode: PropTypes.string,
}

export default Thumbnails
